import React, { useRef, useEffect, useState, useLayoutEffect } from 'react'
import { graphql, StaticQuery } from 'gatsby'
import './Nav.module.scss'
import * as NavStyles from './Nav.module.scss'
import Social from '../../Social/Social'
import { type INavQueryData } from '../../../graphql/queries/NavQuery'
import { headerEvents } from '../../../lib/utils'

const NavQuery = graphql`
  query {
    wpgraphql {
      generalSettings {
        siteId
      }
      secondaryMenu: menu(id: "Secondary", idType: NAME) {
        menuItems {
          nodes {
            id
            url
            label
          }
        }
      }
    }
  }
`

const Nav = () => {
  const [navState, setNavState] = useState<'collapsed' | 'full' | 'hidden'>(
    'full'
  )

  useEffect(() => {
    headerEvents.on('headerState', state => {
      setNavState(state)
    })
  }, [])

  return (
    <StaticQuery
      query={NavQuery}
      render={({
        wpgraphql: {
          secondaryMenu: { menuItems }
        }
      }: INavQueryData) => {
        const menuRef = useRef<HTMLUListElement>(null)
        const [fadeLeft, setFadeLeft] = useState<number | null>(null)
        const [menuItemsList, setMenuItemsList] = useState<any>([])

        const [menuPosition, setMenuPosition] = useState<number>(0)
        const [menuPix, setMenuPix] = useState<number>(30)
        const [menuOffsets, setMenuOffsets] = useState<
          { left: number; width: number }[]
        >([])
        const [isNextButtonVisible, setIsNextButtonVisible] =
          useState<boolean>(true)
        const menuRefs = useRef<(HTMLLIElement | null)[]>([])
        const containerRef = useRef<HTMLUListElement | null>(null)

        // Updating the menu items list
        useEffect(() => {
          const menuItemsClone =
            menuItems?.nodes?.map(menuItem => {
              return {
                ...menuItem
              }
            }) || []

          setMenuItemsList([
            ...menuItemsClone.slice(0, 4),
            {
              id: 'best-schools-awards',
              label: 'BEST SCHOOLS AWARDS',
              url: 'https://muddystilettos.co.uk/best-schools-awards/'
            },
            ...menuItemsClone.slice(4)
          ])
        }, [menuItems])

        // Calculate offsets when menuItemsList changes
        useLayoutEffect(() => {
          setTimeout(() => {
            const offsets = menuRefs.current.map(ref => ({
              left: ref ? ref.offsetLeft : 0,
              width: ref ? ref.offsetWidth : 0
            }))
            setMenuOffsets(offsets)
          }, 1000)
        }, [menuItemsList])

        const isLastItemVisible = (
          lastItem: { left: number; width: number },
          containerWidth: number,
          menuPixels: number
        ) => lastItem.left + lastItem.width - menuPixels <= containerWidth - 60

        // Update the menuPix based on menuPosition
        useEffect(() => {
          if (menuPosition < menuOffsets.length && containerRef.current) {
            const offset = menuOffsets[menuPosition]
            if (offset && offset.left !== menuPix) {
              if (menuPosition === 0) {
                setMenuPix(30)
              } else {
                setMenuPix(offset.left)
              }
            }
          }
        }, [menuPosition, menuOffsets, menuPix])

        // Handle visibility of the next button
        useLayoutEffect(() => {
          if (menuOffsets.length > 0 && containerRef.current) {
            const lastItem = menuOffsets[menuOffsets.length - 1]
            const containerWidth = containerRef.current.offsetWidth

            if (
              isNextButtonVisible !==
              !isLastItemVisible(lastItem, containerWidth, menuPix)
            ) {
              setIsNextButtonVisible(
                !isLastItemVisible(lastItem, containerWidth, menuPix)
              )
            }
          }
        }, [menuOffsets, menuPix, isNextButtonVisible])

        const handleHorizontalScroll = () => {
          const menu = menuRef.current
          if (menu) {
            const newFadeLeft = menu.clientWidth + menu.scrollLeft - 49
            setFadeLeft(newFadeLeft)
          }
        }

        useEffect(() => {
          const menu = menuRef.current
          if (menu) {
            menu.addEventListener('scroll', handleHorizontalScroll)
            handleHorizontalScroll() // Initialize
          }

          return () => {
            if (menu) {
              menu.removeEventListener('scroll', handleHorizontalScroll)
            }
          }
        }, [menuRef.current, navState])

        const fadeStyle: React.CSSProperties =
          fadeLeft !== null ? { left: `${fadeLeft}px` } : { left: '100%' }

        return (
          <div className={NavStyles.SuperWrapper}>
            <nav
              className={NavStyles.Wrapper}
              aria-label="Main"
              ref={containerRef}
            >
              <div className={NavStyles.MenuButtons}>
                {menuPosition > 0 && (
                  <div
                    className={NavStyles.Prev}
                    aria-label="Previous"
                    onClick={() =>
                      setMenuPosition(Math.max(0, menuPosition - 1))
                    }
                  >
                    &lt;
                  </div>
                )}
                {isNextButtonVisible && (
                  <div
                    className={NavStyles.Next}
                    aria-label="Next"
                    onClick={() => setMenuPosition(menuPosition + 1)}
                  >
                    &gt;
                  </div>
                )}
              </div>
              <ul
                className={NavStyles.MainNav}
                style={{ left: `-${menuPix}px` }}
              >
                {menuItemsList.map((menuItem, i) => (
                  <li
                    style={{ order: i }}
                    key={menuItem.id}
                    ref={el => {
                      menuRefs.current[i] = el
                    }}
                  >
                    <a href={menuItem.url || ''}>{menuItem.label}</a>
                  </li>
                ))}
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                <div className={NavStyles.FadeOverlay} style={fadeStyle}></div>
              </ul>
            </nav>
          </div>
        )
      }}
    />
  )
}

export default Nav
